<template>
	<aside class="clip-panel flex w-full min-w-0 flex-col gap-4 rounded-lg bg-surface-2 p-4">
		<!-- Quick clip -->
		<section class="max-lg:sticky max-lg:top-0 max-lg:z-10 max-lg:-mx-4 max-lg:-mt-4 max-lg:bg-surface-2 max-lg:px-4 max-lg:pt-4 max-lg:pb-3 lg:pb-0">
			<div class="mb-2 flex items-center justify-between gap-3">
				<h3 class="m-0 text-sm font-medium text-surface-text-2">Select previous</h3>
				<span class="min-w-0 truncate text-xs tabular-nums text-surface-8">{{ quickClipHint }}</span>
			</div>
			<div class="grid grid-cols-3 gap-2">
				<Tooltip
					v-for="q in QUICK_CLIPS"
					:key="q.seconds"
					:content="quickClipDisabledReason"
					:active="!!quickClipDisabledReason"
					wrap-trigger
					wrapper-class="w-full"
				>
					<FormButton
						type="secondary"
						size="md"
						full-width
						:disabled="!!quickClipDisabledReason"
						:title="quickClipDisabledReason ? '' : `Select the previous ${q.human} ${quickClipAnchorText}`"
						@click="quickClip(q)"
					>
						Previous {{ q.label }}
					</FormButton>
				</Tooltip>
			</div>
		</section>

		<div class="h-px w-full bg-surface-3" />

		<!-- In / Out -->
		<section class="flex flex-col gap-3">
			<div class="flex items-center justify-between">
				<h3 class="m-0 text-sm font-medium text-surface-text-2">Clip range</h3>
				<FormButton v-if="selection" type="link" size="xs" @click="clearSelection">Clear</FormButton>
			</div>

			<!-- In / Out frames (refresh on committed changes, not on every drag move) -->
			<div class="grid grid-cols-2 gap-2">
				<button
					v-for="row in ROWS"
					:key="`thumb-${row.key}`"
					type="button"
					class="group relative aspect-video w-full overflow-hidden rounded-md bg-black ring-1 ring-surface-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-main disabled:cursor-default"
					:disabled="!committedSelection"
					:title="committedSelection ? `Jump to the ${row.label.toLowerCase()} point (${formatLocalTime(committedSelection[row.key])})` : ''"
					:aria-label="committedSelection ? `Jump to the ${row.label.toLowerCase()} point at ${formatLocalTime(committedSelection[row.key])}` : `Clip ${row.label.toLowerCase()} frame`"
					@click="committedSelection && $emit('seek', committedSelection[row.key])"
				>
					<img
						v-if="committedSelection && edgeThumbs[row.key].src && !edgeThumbs[row.key].broken"
						:src="edgeThumbs[row.key].src"
						alt=""
						class="h-full w-full object-cover transition-opacity group-hover:opacity-80"
						@error="onEdgeThumbError(row.key)"
					>
					<span v-else class="flex h-full w-full items-center justify-center px-2 text-center text-[11px] text-surface-8">
						{{ committedSelection ? 'Frame not available' : `No ${row.label.toLowerCase()} point yet` }}
					</span>
					<span class="pointer-events-none absolute left-1.5 top-1.5 rounded bg-black/75 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-white">{{ row.label }}</span>
					<span class="pointer-events-none absolute bottom-1.5 right-1.5 min-w-[62px] rounded bg-black/75 px-1.5 py-px text-right font-mono text-[11px] tabular-nums text-white">
						{{ committedSelection ? formatLocalTime(committedSelection[row.key]) : '--:--:--' }}
					</span>
				</button>
			</div>

			<div v-for="row in ROWS" :key="row.key" class="grid grid-cols-[64px_1fr_auto] items-center gap-2">
				<FormButton
					type="secondary"
					size="sm"
					:disabled="typeof playhead !== 'number' || !hasDvr"
					:title="`Set clip ${row.label.toLowerCase()} at the playhead (${row.shortcut})`"
					@click="row.key === 'start' ? setInAtPlayhead() : setOutAtPlayhead()"
				>
					Set {{ row.label }}
				</FormButton>
				<FormInput
					v-model="timeText[row.key]"
					size="sm"
					:disabled="!selection"
					:is-error="timeError[row.key]"
					:placeholder="selection ? '' : '--:--:--'"
					input-classes="font-mono tabular-nums text-center"
					:aria-label="`Clip ${row.label.toLowerCase()} time`"
					@focus="timeFocused[row.key] = true"
					@blur="commitTime(row.key)"
					@keydown="onTimeKeydown($event, row.key)"
				/>
				<div class="flex items-center gap-1">
					<!-- Hold to repeat (1s steps, then 5s, then 15s). Shift+click jumps 30s. -->
					<!-- The ::after box widens the hit area to 44px without growing the visual. -->
					<FormButton
						v-for="dir in [-1, 1]"
						:key="dir"
						type="secondary"
						size="sm"
						is-icon
						class="relative !overflow-visible touch-none select-none after:absolute after:-inset-1.5 after:content-['']"
						:disabled="!selection"
						:aria-label="`Move ${row.label.toLowerCase()} ${dir < 0 ? 'earlier' : 'later'}. Hold to repeat, Shift for 30 seconds`"
						:title="`${dir < 0 ? 'Earlier' : 'Later'} by 1s. Hold to repeat, Shift+click for 30s (${dir < 0 ? (row.key === 'start' ? 'Shift+[' : 'Shift+]') : (row.key === 'start' ? '[' : ']')})`"
						@pointerdown="onNudgePointerDown($event, row.key, dir)"
						@pointerup="stopNudgeHold"
						@pointerleave="stopNudgeHold"
						@pointercancel="stopNudgeHold"
						@keydown="onNudgeKeydown($event, row.key, dir)"
						@click.prevent
					>
						<svg v-if="dir < 0" width="12" height="12" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg>
						<svg v-else width="12" height="12" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8h10M8 3v10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" /></svg>
					</FormButton>
				</div>
			</div>

			<!-- Selected vs exported -->
			<dl class="m-0 flex flex-col gap-1 text-xs">
				<div class="flex items-baseline justify-between gap-3">
					<dt class="shrink-0 text-surface-8">Selected</dt>
					<dd class="m-0 truncate font-mono tabular-nums" :class="exceedsLimit ? 'text-error-light' : 'text-surface-text-2'">
						<template v-if="committedSelection">
							{{ formatLocalTime(committedSelection.start) }}–{{ formatLocalTime(committedSelection.end) }} · {{ formatClock(committedSelection.end - committedSelection.start) }}
						</template>
						<template v-else>No clip selected</template>
					</dd>
				</div>
				<div class="flex items-baseline justify-between gap-3">
					<dt class="shrink-0 text-surface-8">Exported</dt>
					<dd class="m-0 truncate font-mono tabular-nums text-surface-text-2" :title="exportWindow ? 'The file starts and ends on source segment boundaries' : ''">
						<template v-if="exportWindow">
							{{ formatLocalTime(exportWindow.start) }}–{{ formatLocalTime(exportWindow.end) }} · ≈{{ formatClock(exportWindow.end - exportWindow.start) }}<template v-if="exportSizeBytes"> · ≈{{ formatBytes(exportSizeBytes) }}</template>
						</template>
						<template v-else-if="committedSelection">Not available for this range</template>
						<template v-else>--</template>
					</dd>
				</div>
			</dl>
			<p class="m-0 text-xs text-surface-8">Up to {{ maxMinutesLabel }} per clip on your plan.</p>

			<Alert v-if="exceedsLimit" type="warning2" hide-icon>
				<span>
					This clip is {{ formatDurationHuman(selectionDuration) }}. Your plan allows up to {{ maxMinutesLabel }}.
					Shorten it or
					<button type="button" class="underline hover:text-white" @click="contactSales">contact us</button>
					to raise the limit.
				</span>
			</Alert>
			<div
				v-else-if="gapConflict"
				class="flex flex-col gap-2 rounded-md border border-error/40 bg-error/10 p-3 text-xs text-surface-text-2"
				role="alert"
			>
				<span class="flex items-start gap-2">
					<span class="mt-px shrink-0 font-semibold uppercase tracking-wide text-error-light">Gap</span>
					<span>{{ gapConflict.message }}</span>
				</span>
				<div v-if="gapConflict.options.length" class="flex flex-wrap gap-2">
					<FormButton
						v-for="opt in gapConflict.options"
						:key="opt.label"
						type="secondary"
						size="xs"
						@click="applyRange(opt.range)"
					>
						Use {{ opt.label }}
					</FormButton>
				</div>
			</div>

			<div class="flex h-6 items-center justify-between" :title="canPreview ? 'Play the clip on repeat in the player' : 'Select a clip inside one recording to loop it'">
				<FormSwitch
					:model-value="preview"
					label="Loop clip"
					size="sm"
					label-styles="text-tiny text-surface-text-1"
					:disabled="!canPreview"
					@update:model-value="$emit('update:preview', $event)"
				/>
			</div>
		</section>

		<div class="h-px w-full bg-surface-3" />

		<!-- Actions -->
		<section class="flex flex-col gap-2">
			<FormButton
				type="main"
				size="lg"
				full-width
				:disabled="!!downloadDisabledReason"
				:title="downloadDisabledReason || `Download the exported range (${modKeyLabel}+Enter)`"
				@click="download('manual')"
			>
				<icon-base class="mr-2" fill="none"><icon-download /></icon-base>
				Download clip
			</FormButton>
			<p v-if="downloadDisabledReason" class="m-0 text-xs text-surface-8" role="status">{{ downloadDisabledReason }}</p>
			<FormButton
				type="secondary"
				size="md"
				full-width
				class="tabular-nums"
				:disabled="!canScreenshot"
				:title="canScreenshot ? 'Save the frame at the playhead as a PNG' : 'Available once the video is playing'"
				@click="$emit('screenshot')"
			>
				<icon-base class="mr-2"><icon-camera /></icon-base>
				Save current frame<template v-if="canScreenshot && typeof playhead === 'number'"> · {{ formatLocalTime(playhead) }}</template>
			</FormButton>
			<p class="m-0 text-xs text-surface-8">Exports align to source segments and may include a few extra seconds at either edge.</p>
		</section>

		<!-- Recent downloads -->
		<section v-if="recent.length" class="flex flex-col gap-2">
			<div class="h-px w-full bg-surface-3" />
			<div class="flex items-center justify-between">
				<h3 class="m-0 text-sm font-medium text-surface-text-2">Recent downloads</h3>
				<FormButton type="link" size="xs" @click="clearRecent">Clear</FormButton>
			</div>
			<ul class="m-0 flex list-none flex-col gap-1 p-0">
				<li
					v-for="item in recent"
					:key="item.id"
					class="flex items-center justify-between gap-2 rounded-md bg-surface-3 px-3 py-2 text-tiny"
				>
					<div class="min-w-0">
						<div class="truncate font-mono text-xs text-surface-text-2" :title="item.fileName">{{ item.fileName }}</div>
						<div class="font-mono text-xs tabular-nums text-surface-8">
							{{ formatLocalTime(item.exportStart) }} to {{ formatLocalTime(item.exportEnd) }}
							· {{ formatClock(item.exportEnd - item.exportStart) }}<template v-if="item.sizeBytes"> · ≈{{ formatBytes(item.sizeBytes) }}</template>
						</div>
						<div class="text-xs text-surface-8">
							<template v-if="recentExpired(item)">No longer in the recording window</template>
							<template v-else>Available for {{ formatDurationHuman(recentSecondsLeft(item)) }}</template>
						</div>
					</div>
					<FormButton
						type="secondary"
						size="sm"
						:disabled="recentExpired(item)"
						:title="recentExpired(item) ? 'The recording has expired' : 'Download again'"
						@click="downloadAgain(item)"
					>
						Download again
					</FormButton>
				</li>
			</ul>
		</section>
	</aside>
</template>

<script setup>
import { ref, computed, watch, reactive, onBeforeUnmount } from 'vue';
import FormButton from '@/components/Atoms/FormButton.vue';
import FormInput from '@/components/Atoms/FormInput.vue';
import FormSwitch from '@/components/Atoms/FormSwitch.vue';
import Alert from '@/components/Atoms/Alert.vue';
import Tooltip from '@/components/Atoms/Tooltip.vue';
import IconBase from '@/components/icon/IconBase.vue';
import IconDownload from '@/components/icon/IconDownload.vue';
import IconCamera from '@/components/icon/IconCamera.vue';
import { track, EVENTS } from '@/lib/tracking';
import { createHoldRepeater } from './holdRepeat';
import {
	LIVE_EDGE_SAFETY_SEC,
	selectionIsContiguous,
	selectionGapConflict,
	rangeAt,
	downloadUrl,
	serverFileName,
	thumbnailUrl,
	estimateSizeBytes,
	formatBytes,
	formatClock,
	formatDurationHuman,
	formatLocalTime,
	parseLocalTime,
	clamp,
} from './dvrUtils';

const props = defineProps({
	selection: { type: Object, default: null },
	preview: { type: Boolean, default: false },
	ranges: { type: Array, default: () => [] },
	liveEdge: { type: Number, default: null },
	// True while the player follows the live edge (quick clips end at live).
	playingLive: { type: Boolean, default: true },
	playhead: { type: Number, default: null },
	maxDuration: { type: Number, default: 900 },
	hasDvr: { type: Boolean, default: false },
	baseUrl: { type: String, default: '' },
	highlightId: { type: String, default: '' },
	streamId: { type: String, default: '' },
	canScreenshot: { type: Boolean, default: false },
	// { start, end } of the whole DVR window (recording_status).
	dvrExtent: { type: Object, default: null },
	// Selection as of the last committed change (drag end, field commit,
	// release of a held nudge). Thumbnails and the readout follow this one so
	// they do not refetch on every tick.
	committedSelection: { type: Object, default: null },
	// { start, end } the origin will deliver for committedSelection, or null.
	exportWindow: { type: Object, default: null },
	// video + audio kbps from media_info.json for size estimates.
	bitrateKbps: { type: Number, default: null },
});

const emit = defineEmits([
	'update:selection',
	'update:preview',
	'screenshot',
	'downloaded',
	'notify',
	'seek',
	// true while a nudge button is held, false on release.
	'interaction',
]);

const QUICK_CLIPS = [
	{ seconds: 30, label: '30s', human: '30 seconds', source: 'quick30' },
	{ seconds: 60, label: '1m', human: 'minute', source: 'quick60' },
	{ seconds: 300, label: '5m', human: '5 minutes', source: 'quick300' },
];

const modKeyLabel = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || '') ? 'Cmd' : 'Ctrl';

const ROWS = [
	{ key: 'start', label: 'In', shortcut: 'I' },
	{ key: 'end', label: 'Out', shortcut: 'O' },
];

const RECENT_MAX = 8;

// In-memory only: the list lives for this page session.
const recent = ref([]);
const timeText = reactive({ start: '', end: '' });
const timeError = reactive({ start: false, end: false });
const timeFocused = reactive({ start: false, end: false });

const selectionDuration = computed(() => (props.selection ? props.selection.end - props.selection.start : 0));
const exceedsLimit = computed(() => !!props.selection && selectionDuration.value > props.maxDuration + 0.001);
const contiguous = computed(() => selectionIsContiguous(props.ranges, props.selection));

// Why the selection cannot be exported in one file, plus one-click fixes.
const gapConflict = computed(() => {
	const c = selectionGapConflict(props.ranges, props.selection);
	if (!c) return null;
	const { gap, before, after } = c;
	const label = (r) => `${formatLocalTime(r.start)} to ${formatLocalTime(r.end)}`;
	const options = [];
	if (before) options.push({ label: label(before), range: before });
	if (after) options.push({ label: label(after), range: after });
	let message;
	if (!Number.isFinite(gap.start)) {
		message = `Your selection starts before the recording begins (${formatLocalTime(gap.end)}).`;
	} else if (!Number.isFinite(gap.end)) {
		message = `Your selection runs past the end of the recording (${formatLocalTime(gap.start)}).`;
	} else {
		message = `Your selection crosses a gap in the recording (${formatLocalTime(gap.start)} to ${formatLocalTime(gap.end)}).`;
	}
	if (options.length === 2) message += ` Choose ${options[0].label} or ${options[1].label}.`;
	else if (options.length === 1) message += ` Choose ${options[0].label}.`;
	else message += ' Pick a spot inside a recorded section.';
	return { message, options };
});

function applyRange(range) {
	if (!range) return;
	const { end } = range;
	let { start } = range;
	if (end - start > props.maxDuration) start = end - props.maxDuration;
	setSelection({ start, end, source: props.selection?.source });
}

const exportSizeBytes = computed(() => (props.exportWindow
	? estimateSizeBytes(props.exportWindow.end - props.exportWindow.start, props.bitrateKbps)
	: null));

// In / Out frames. Thumbnails resolve to the keyframe at or after the
// requested second; for the out point ask one second earlier so the frame
// sits inside the clip.
const edgeThumbs = reactive({
	start: { t: null, src: '', broken: false, tries: 0 },
	end: { t: null, src: '', broken: false, tries: 0 },
});
// Frames right behind the live edge are written a few seconds late; retry
// with a cache-busting suffix before showing the placeholder.
const THUMB_RETRY_MS = [2500, 5000, 8000];
const thumbRetryTimers = { start: null, end: null };

function thumbSrcFor(key) {
	const th = edgeThumbs[key];
	if (th.t === null || !props.baseUrl) return '';
	const base = thumbnailUrl(props.baseUrl, th.t);
	return th.tries ? `${base}?r=${th.tries}` : base;
}

function onEdgeThumbError(key) {
	const th = edgeThumbs[key];
	clearTimeout(thumbRetryTimers[key]);
	if (th.tries >= THUMB_RETRY_MS.length) {
		th.broken = true;
		return;
	}
	const expected = th.t;
	thumbRetryTimers[key] = setTimeout(() => {
		if (edgeThumbs[key].t !== expected) return;
		edgeThumbs[key].tries += 1;
		edgeThumbs[key].src = thumbSrcFor(key);
	}, THUMB_RETRY_MS[th.tries]);
}

watch(() => props.committedSelection, (sel) => {
	for (const key of ['start', 'end']) {
		const t = sel ? (key === 'end' ? Math.max(sel.start, Math.ceil(sel.end) - 1) : Math.floor(sel.start)) : null;
		if (t === edgeThumbs[key].t) continue;
		clearTimeout(thumbRetryTimers[key]);
		edgeThumbs[key].t = t;
		edgeThumbs[key].tries = 0;
		edgeThumbs[key].broken = false;
		edgeThumbs[key].src = thumbSrcFor(key);
	}
}, { immediate: true, deep: true });
const canPreview = computed(() => !!props.selection && contiguous.value);

const maxMinutesLabel = computed(() => {
	const mins = props.maxDuration / 60;
	if (mins >= 60 && mins % 60 === 0) return `${mins / 60} ${mins === 60 ? 'hour' : 'hours'}`;
	const rounded = Math.round(mins * 10) / 10;
	return `${rounded} min`;
});

const quickClipEnd = computed(() => {
	if (!props.hasDvr) return null;
	if (!props.playingLive && typeof props.playhead === 'number') return props.playhead;
	if (typeof props.liveEdge === 'number') return props.liveEdge - LIVE_EDGE_SAFETY_SEC;
	return null;
});

const quickClipDisabledReason = computed(() => {
	if (!props.hasDvr) return 'Quick clips become available once the stream has been recorded.';
	if (quickClipEnd.value === null) return 'Waiting for the recording position.';
	return '';
});

const quickClipAnchorText = computed(() => (props.playingLive ? 'before the live edge' : 'before the playhead'));

const quickClipHint = computed(() => {
	if (!props.hasDvr || quickClipEnd.value === null) return '';
	const anchor = props.playingLive ? 'Ending at live edge' : 'Ending at playhead';
	return `${anchor} · ${formatLocalTime(quickClipEnd.value)}`;
});

const downloadDisabledReason = computed(() => {
	if (!props.selection) return 'Select a range on the timeline or use a quick clip first.';
	if (!contiguous.value) return 'The clip crosses a gap in the recording.';
	if (exceedsLimit.value) return `Clips are limited to ${maxMinutesLabel.value} on your plan.`;
	if (selectionDuration.value < 1) return 'The clip is too short.';
	return '';
});

function setSelection(sel) {
	emit('update:selection', sel);
}

function clearSelection() {
	setSelection(null);
	if (props.preview) emit('update:preview', false);
}

function notify(group, title, text) {
	emit('notify', { group, title, text });
}

function quickClip(q) {
	const end = quickClipEnd.value;
	if (end === null) return;
	if (!rangeAt(props.ranges, end)) {
		notify('error', 'Nothing recorded there yet', 'Try again in a few seconds or pick a spot on the timeline.');
		return;
	}
	// Do not shorten silently: a quick clip that reaches across a gap shows
	// the gap explanation with the valid alternatives instead.
	const lo = props.dvrExtent?.start ?? -Infinity;
	const start = Math.max(lo, end - Math.min(q.seconds, props.maxDuration));
	setSelection({ start, end, source: q.source });
}

function setInAtPlayhead() {
	const t = props.playhead;
	if (typeof t !== 'number') return;
	const sel = props.selection;
	if (sel && t < sel.end && rangeAt(props.ranges, sel.end) === rangeAt(props.ranges, t)) {
		setSelection({ start: Math.max(t, sel.end - props.maxDuration), end: sel.end });
		return;
	}
	const range = rangeAt(props.ranges, t);
	if (!range) {
		notify('error', 'No recording at the playhead');
		return;
	}
	const end = Math.min(range.end, t + Math.min(props.maxDuration, 30));
	if (end - t < 1) return;
	setSelection({ start: t, end });
}

function setOutAtPlayhead() {
	const t = props.playhead;
	if (typeof t !== 'number') return;
	const sel = props.selection;
	if (sel && t > sel.start && rangeAt(props.ranges, sel.start) === rangeAt(props.ranges, t)) {
		setSelection({ start: Math.max(sel.start, t - props.maxDuration), end: t });
		return;
	}
	const range = rangeAt(props.ranges, t);
	if (!range) {
		notify('error', 'No recording at the playhead');
		return;
	}
	const start = Math.max(range.start, t - Math.min(props.maxDuration, 30));
	if (t - start < 1) return;
	setSelection({ start, end: t });
}

function boundsFor(which) {
	const sel = props.selection;
	// Inside one recording the edges stop at its boundaries; a selection that
	// crosses a gap may be edited freely so it can be pulled back into one.
	const range = contiguous.value
		? rangeAt(props.ranges, sel.start)
		: { start: props.dvrExtent?.start ?? -Infinity, end: typeof props.liveEdge === 'number' ? props.liveEdge : Infinity };
	if (which === 'start') return { min: Math.max(range.start, sel.end - props.maxDuration), max: sel.end - 1 };
	return { min: sel.start + 1, max: Math.min(range.end, sel.start + props.maxDuration) };
}

function setEdge(which, t) {
	const sel = props.selection;
	if (!sel) return false;
	const b = boundsFor(which);
	const v = clamp(t, b.min, b.max);
	if (which === 'start') setSelection({ start: v, end: sel.end });
	else setSelection({ start: sel.start, end: v });
	return Math.abs(v - t) < 0.5;
}

function nudge(which, dt) {
	if (!props.selection) return;
	setEdge(which, props.selection[which] + dt);
}

// Press-and-hold on the nudge buttons: 1s steps after 400ms, then 5s, 15s.
const nudgeHold = createHoldRepeater({
	onStart: () => emit('interaction', true),
	onStep: (dt, { which }) => nudge(which, dt),
	onEnd: () => emit('interaction', false),
});

function onNudgePointerDown(e, which, dir) {
	if (!props.selection || e.button !== 0) return;
	e.preventDefault(); // keep focus where it is; no text selection on touch
	if (e.shiftKey) {
		nudge(which, 30 * dir);
		return;
	}
	nudgeHold.start(dir, { which });
}

function stopNudgeHold() {
	nudgeHold.stop();
}

function onNudgeKeydown(e, which, dir) {
	if (e.key !== 'Enter' && e.key !== ' ') return;
	e.preventDefault();
	if (e.repeat) return;
	nudge(which, (e.shiftKey ? 30 : 1) * dir);
}

function syncTimeText() {
	if (!props.selection) {
		timeText.start = '';
		timeText.end = '';
		timeError.start = false;
		timeError.end = false;
		return;
	}
	if (!timeFocused.start) timeText.start = formatLocalTime(props.selection.start);
	if (!timeFocused.end) timeText.end = formatLocalTime(props.selection.end);
}

function commitTime(which) {
	timeFocused[which] = false;
	if (!props.selection) return;
	const parsed = parseLocalTime(timeText[which], props.selection[which]);
	if (parsed === null) {
		timeError[which] = true;
		timeText[which] = formatLocalTime(props.selection[which]);
		setTimeout(() => { timeError[which] = false; }, 1500);
		return;
	}
	const exact = setEdge(which, parsed);
	timeError[which] = false;
	if (!exact) notify('warning', 'Time adjusted', 'The clip edge was moved to stay inside the recording and plan limit.');
	syncTimeText();
}

function onTimeKeydown(e, which) {
	if (e.key === 'Enter') {
		e.preventDefault();
		e.target?.blur?.();
	} else if (e.key === 'Escape') {
		timeText[which] = props.selection ? formatLocalTime(props.selection[which]) : '';
		e.target?.blur?.();
	} else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
		e.preventDefault();
		nudge(which, e.key === 'ArrowUp' ? 1 : -1);
	}
}

// The origin answers with Content-Disposition: attachment, so pointing a
// hidden iframe at the URL saves the file without leaving (or opening) a tab.
let downloadFrame = null;
let downloadFrameTimer = null;
function triggerDownload(url) {
	if (typeof document === 'undefined') return;
	if (!downloadFrame) {
		downloadFrame = document.createElement('iframe');
		downloadFrame.setAttribute('aria-hidden', 'true');
		downloadFrame.tabIndex = -1;
		downloadFrame.style.cssText = 'position:fixed;width:0;height:0;border:0;opacity:0;pointer-events:none;';
		document.body.appendChild(downloadFrame);
	}
	downloadFrame.src = url;
	clearTimeout(downloadFrameTimer);
	// Give the browser a generous window to start streaming before we drop it.
	downloadFrameTimer = setTimeout(() => {
		downloadFrame?.remove();
		downloadFrame = null;
	}, 120000);
}

function download(sourceOverride) {
	if (downloadDisabledReason.value || !props.baseUrl) return;
	const { start, end } = props.selection;
	const url = downloadUrl(props.baseUrl, start, end);
	triggerDownload(url);
	notify('success', 'Download started', 'Your browser is saving the clip.');

	const source = props.selection.source || sourceOverride || 'manual';
	const durationSec = Math.ceil(end - start);
	track(EVENTS.HIGHLIGHT_CLIP_DOWNLOADED, {
		highlightId: props.highlightId,
		streamId: props.streamId,
		durationSec,
		source,
	});

	const exp = props.exportWindow || { start, end };
	const entry = {
		id: `${Math.floor(start)}-${durationSec}-${Date.now()}`,
		fileName: serverFileName(props.baseUrl, start, end),
		start,
		end,
		exportStart: exp.start,
		exportEnd: exp.end,
		sizeBytes: exportSizeBytes.value,
		url,
		at: Date.now(),
	};
	recent.value = [entry, ...recent.value].slice(0, RECENT_MAX);
	emit('downloaded', entry);
}

function downloadAgain(item) {
	if (recentExpired(item)) return;
	triggerDownload(item.url);
	notify('success', 'Download started', 'Your browser is saving the clip.');
}

function recentSecondsLeft(item) {
	const ext = props.dvrExtent;
	if (!ext) return Infinity;
	return Math.max(0, item.exportStart - ext.start);
}

function recentExpired(item) {
	const ext = props.dvrExtent;
	if (!ext) return false;
	return item.exportStart < ext.start;
}

function clearRecent() {
	recent.value = [];
}

watch(() => props.highlightId, () => {
	recent.value = [];
});

onBeforeUnmount(() => {
	nudgeHold.stop(false);
	clearTimeout(thumbRetryTimers.start);
	clearTimeout(thumbRetryTimers.end);
	clearTimeout(downloadFrameTimer);
	downloadFrame?.remove();
	downloadFrame = null;
});

function contactSales() {
	if (typeof window !== 'undefined' && window.Intercom) {
		window.Intercom('show');
	} else {
		window.open('https://castr.com/contact/', '_blank', 'noopener');
	}
}

watch(() => props.selection, syncTimeText, { immediate: true, deep: true });

watch(canPreview, (ok) => {
	if (!ok && props.preview) emit('update:preview', false);
});

defineExpose({ download, quickClip });
</script>
